// core/stores/typingStore.ts — realtime "is typing" indicator per conversation.
//
// Kept separate from messageStore so messageStore's existing tests (and its
// persistence-free, purely-derived-from-server-payload shape) stay untouched.
//
// Each entry auto-clears itself 5s after the last `typing` event for that
// conversation via a real setTimeout (fake-timer friendly — no Date.now()
// polling loop). A second event for the same conversation before the 5s
// window elapses resets the window instead of stacking a second pending clear.

import { create } from 'zustand';

interface TypingEntry {
  handle: string;
  expiresAt: number;
}

interface TypingState {
  typing: Record<string, TypingEntry | undefined>;

  /** Record that `handle` is typing in `conversationId`; schedules a 5s auto-clear. */
  setTyping: (conversationId: string, handle: string) => void;

  /** Clear a conversation's typing entry immediately (used internally by the timeout). */
  clearTyping: (conversationId: string) => void;
}

// Pending auto-clear timers, keyed by conversation id — module state, not store
// state, since a timer handle isn't serialisable/comparable Zustand state.
const clearTimers = new Map<string, ReturnType<typeof setTimeout>>();

const TYPING_TTL_MS = 5000;

export const useTypingStore = create<TypingState>()((set) => ({
  typing: {},

  setTyping: (conversationId, handle) => {
    const existing = clearTimers.get(conversationId);
    if (existing) clearTimeout(existing);

    const timer = setTimeout(() => {
      clearTimers.delete(conversationId);
      useTypingStore.getState().clearTyping(conversationId);
    }, TYPING_TTL_MS);
    clearTimers.set(conversationId, timer);

    set((s) => ({
      typing: { ...s.typing, [conversationId]: { handle, expiresAt: Date.now() + TYPING_TTL_MS } },
    }));
  },

  clearTyping: (conversationId) => {
    // W10 (1.21.00 review): a caller-initiated clear (e.g. the dm.message
    // handler clearing typing the moment the actual message arrives) must
    // also cancel any pending auto-clear timer for this conversation — left
    // dangling, that stale timer would later fire and clear whatever NEW
    // typing entry had been set in the meantime.
    const pending = clearTimers.get(conversationId);
    if (pending) {
      clearTimeout(pending);
      clearTimers.delete(conversationId);
    }
    set((s) => {
      if (!(conversationId in s.typing)) return s;
      const next = { ...s.typing };
      delete next[conversationId];
      return { typing: next };
    });
  },
}));

/** UI hook: the handle currently typing in `conversationId`, or null. */
export function useTypingIndicator(conversationId: string): string | null {
  const entry = useTypingStore((s) => s.typing[conversationId]);
  return entry?.handle ?? null;
}
